import React from "react";
import { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";

import { getWhitelistedCIDRs, addWhitelistCIDR, deleteWhitelistedCIDR } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
// core components
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import { AuthContext } from "contexts/authContext";
import { tourGuideSteps } from "views/Reports/ReportsSteps";
import { useTour } from "@reactour/tour";

// react-bootstrap components
import {
  Container,
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Form,
  Nav,
  Tab,
} from "react-bootstrap";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import _ from "lodash";

function ManageFirewall(props) {
  // Hooks
  const notify = useContext(NotificationAlertContext);
  // const currentUser = useContext(AuthContext);
  const { clientId, clientEnvironmentId } = useParams();
  const { clientName, clientEnvironmentName } = props.location.state;

  // States
  const [ipv4TableData, setIpv4TableData] = useState([]);
  const [ipv6TableData, setIpv6TableData] = useState([]);
  const [ipv4TokenData, setIpv4TokenData] = useState("");
  const [ipv6TokenData, setIpv6TokenData] = useState("");
  const [activeIpTab, setActiveIpTab] = useState("ipv4");
  const [showLoader, setShowLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [selectedIps, setSelectedIps] = React.useState([]);

  // State for Add CIDR Modal
  const [addCidrModalOpen, setAddCidrModalOpen] = useState(false);
  const [cidrValue, setCidrValue] = useState("");
  const [notesValue, setNotesValue] = useState("");

  // State for Delete CIDR Modal
  const [deleteCidrModalOpen, setDeleteCidrModalOpen] = useState(false);

  const headers = [
    { key: "cidr", label: "CIDR" },
    { key: "note", label: "Notes" },
  ];

  // Tour Guide
  // const { setIsOpen, setCurrentStep, setSteps } = useTour();

  // Pagination Starts
  const columnMapping = {
    CIDR: "cidr",
    Notes: "note",
  };

  const numberOfRowsData = [25, 50, 100];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[0]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("Scheduled Date");
  const [alert, setAlert] = useState(null);

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const fetchWhitelistedIps = async (
    filterSearchObj = {},
    isCSVDownload = false
  ) => {
    try {
      setShowLoader(true);
      let data = {
        client_environment_id: clientEnvironmentId,
      };
      const response = await API.graphql(
        graphqlOperation(getWhitelistedCIDRs, data)
      );
      if (response.data?.getWhitelistedCIDRs?.success) {
        const data = response.data.getWhitelistedCIDRs.body;

        if (!isCSVDownload) {
          setIpv4TableData(data.ipv4_cidrs.cidrs);
          setIpv6TableData(data.ipv6_cidrs.cidrs);
          setIpv4TokenData(data.ipv4_cidrs.lock_token);
          setIpv6TokenData(data.ipv6_cidrs.lock_token);
          notify("success");
        } else {
          const messageDataForCSV =
            activeIpTab === "ipv4" ? ipv4TableData : ipv6TableData;
          return { messageDataForCSV, filterSearchObj };
        }
      } else {
        console.error(response.data?.getWhitelistedCIDRs?.message);
      }
      return response.data?.getWhitelistedCIDRs?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const addCidr = async () => {
    try {
      setShowLoader(true);
      const ipAddressVersion = activeIpTab === "ipv4" ? "IPV4" : "IPV6";
      const lockToken = activeIpTab === "ipv4" ? ipv4TokenData : ipv6TokenData;

      const data = {
        client_environment_id: clientEnvironmentId,
        ip_address_version: ipAddressVersion,
        cidr: cidrValue,
        note: notesValue,
        lock_token: lockToken,
      };

      const response = await API.graphql(
        graphqlOperation(addWhitelistCIDR, data)
      );

      if (response.data?.addWhitelistCIDR?.success) {
        const responseBody = response.data.addWhitelistCIDR.body;
        // Update the lock token with the new one
        if (activeIpTab === "ipv4") {
          setIpv4TokenData(responseBody.next_lock_token);
        } else {
          setIpv6TokenData(responseBody.next_lock_token);
        }
        notify("success", "CIDR added successfully!");
        // Refresh the list
        await fetchWhitelistedIps();
        // Reset form
        setCidrValue("");
        setNotesValue("");
        setAddCidrModalOpen(false);
        return true;
      } else {
        console.error(response.data?.addWhitelistCIDR?.message);
        notify("danger", response.data?.addWhitelistCIDR?.message || "Failed to add CIDR");
        return false;
      }
    } catch (error) {
      console.error(error);
      notify("danger", "An error occurred while adding the CIDR");
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const deleteCidrs = async () => {
    try {
      if (selectedIps.length === 0) {
        notify("danger", "Please select at least one CIDR to delete");
        return false;
      }

      setShowLoader(true);
      const ipAddressVersion = activeIpTab === "ipv4" ? "IPV4" : "IPV6";
      const lockToken = activeIpTab === "ipv4" ? ipv4TokenData : ipv6TokenData;

      const data = {
        client_environment_id: clientEnvironmentId,
        ip_address_version: ipAddressVersion,
        cidrs: selectedIps,
        lock_token: lockToken,
      };

      const response = await API.graphql(
        graphqlOperation(deleteWhitelistedCIDR, data)
      );

      if (response.data?.deleteWhitelistedCIDR?.success) {
        const responseBody = response.data.deleteWhitelistedCIDR.body;
        // Update the lock token with the new one
        if (activeIpTab === "ipv4") {
          setIpv4TokenData(responseBody.next_lock_token);
        } else {
          setIpv6TokenData(responseBody.next_lock_token);
        }
        notify("success", "CIDRs deleted successfully!");
        // Refresh the list
        await fetchWhitelistedIps();
        // Reset selected IPs
        setSelectedIps([]);
        setDeleteCidrModalOpen(false);
        return true;
      } else {
        console.error(response.data?.deleteWhitelistedCIDR?.message);
        notify("danger", response.data?.deleteWhitelistedCIDR?.message || "Failed to delete CIDRs");
        return false;
      }
    } catch (error) {
      console.error(error);
      notify("danger", "An error occurred while deleting the CIDRs");
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const isSuccess = await fetchWhitelistedIps();
      if (!isSuccess) {
        notify("danger");
        setShowError(true);
      }
    }
    fetchData();
  }, []);

  if (showLoader) return <Spinner animation="grow" />;

  if (showError)
    return `Could not load the page at this moment. Please try again later.`;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Manage Firewall</h2>
          </Col>
          <Col className="my-auto text-right">
            <Button
              variant="primary"
              id="delete-cidr-button"
              onClick={() => {
                setAddCidrModalOpen(true);
              }}
            >
              <i className="fas fa-plus"></i> Add CIDR
            </Button>
            <Button
              variant="danger"
              id="delete-cidr-button"
              className="ml-4 btn-outline"
              disabled={selectedIps.length === 0}
              onClick={() => {
                setDeleteCidrModalOpen(true);
              }}
            >
              <i className="fas fa-trash-alt"></i> Delete CIDR
            </Button>
            {/* {currentUser.groupName === "external" && (
              <Button
                variant="secondary"
                className="btn-outline ml-4"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )} */}
          </Col>
        </Row>
        <Row>
          <Col md="12" sm="12" id="environment-details">
            <h5>Client name: {clientName}</h5>
            <h5>Environment Name: {clientEnvironmentName}</h5>
            <h5>Environment Id: {clientEnvironmentId}</h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Link
              className="btn btn-primary btn-outline"
              id="firewall-logs-button"
              to={{
                pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/manage-firewall/firewall-logs`,
                state: {
                  clientEnvironmentName: clientEnvironmentName,
                  clientName: clientName,
                },
              }}
            >
              View Firewall Logs
            </Link>
          </Col>
        </Row>
        <Card>
          <Card.Body>
            <Tab.Container
              id="ip-tabs"
              activeKey={activeIpTab}
              onSelect={(tab) => {
                setActiveIpTab(tab);
                setSelectedIps([]);
              }}
            >
              <Nav role="tablist" variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="ipv4">IPv4</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="ipv6">IPv6</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="ipv4">
                  <Row>
                    <Col>
                      <ReactTable
                        data={ipv4TableData}
                        totalCount={ipv4TableData.length}
                        functionCallBack={fetchWhitelistedIps}
                        makePaginationStatic={true}
                        setStateForPagnination={setStateForPagnination}
                        currentLimit={currentLimit}
                        currentOffset={currentOffset}
                        ascOrDescValue={ascOrDescValue}
                        sortValue={sortValue}
                        numberOfRowsData={numberOfRowsData}
                        columnMapping={columnMapping}
                        tableName="IPv4 IPs"
                        CSVFileName={"Firewall_IPv4_IPs_Export"}
                        CSVHeaders={headers}
                        notify={notify}
                        columns={[
                          {
                            Header: "Select",
                            Cell: (cell) => {
                              const row = cell.row.original;
                              return (
                                <Form.Check
                                  style={{
                                    padding: "0",
                                    position: "relative",
                                    bottom: "20px",
                                  }}
                                >
                                  <Form.Check.Label>
                                    <Form.Check.Input
                                      type="checkbox"
                                      defaultChecked={selectedIps.includes(
                                        row.cidr
                                      )}
                                      onChange={(event) => {
                                        if (event.target.checked) {
                                          setSelectedIps((prev) => [
                                            ...prev,
                                            row.cidr,
                                          ]);
                                        } else {
                                          setSelectedIps((prev) =>
                                            prev.filter(
                                              (cidr) => cidr !== row.cidr
                                            )
                                          );
                                        }
                                      }}
                                    ></Form.Check.Input>
                                    <span className="form-check-sign"></span>
                                  </Form.Check.Label>
                                </Form.Check>
                              );
                            },
                            filterable: false,
                            sortable: false,
                            maxWidth: 80,
                          },
                          {
                            Header: "CIDR",
                            accessor: "cidr",
                            filterable: false,
                            showTooltip: true,
                            sortable: true,
                          },
                          {
                            Header: "Notes",
                            accessor: "note",
                            filterable: false,
                            showTooltip: true,
                            sortable: true,
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="ipv6">
                  <Row>
                    <Col>
                      <ReactTable
                        data={ipv6TableData}
                        totalCount={ipv6TableData.length}
                        functionCallBack={fetchWhitelistedIps}
                        hidePagination={true}
                        setStateForPagnination={setStateForPagnination}
                        currentLimit={currentLimit}
                        currentOffset={currentOffset}
                        ascOrDescValue={ascOrDescValue}
                        sortValue={sortValue}
                        numberOfRowsData={numberOfRowsData}
                        columnMapping={columnMapping}
                        tableName="IPv6 IPs"
                        CSVFileName={"Firewall_IPv6_IPs_Export"}
                        CSVHeaders={headers}
                        notify={notify}
                        columns={[
                          {
                            Header: "Select",
                            Cell: (cell) => {
                              const row = cell.row.original;
                              return (
                                <Form.Check
                                  style={{
                                    padding: "0",
                                    position: "relative",
                                    bottom: "20px",
                                  }}
                                >
                                  <Form.Check.Label>
                                    <Form.Check.Input
                                      type="checkbox"
                                      defaultChecked={selectedIps.includes(
                                        row.cidr
                                      )}
                                      onChange={(event) => {
                                        if (event.target.checked) {
                                          setSelectedIps((prev) => [
                                            ...prev,
                                            row.cidr,
                                          ]);
                                        } else {
                                          setSelectedIps((prev) =>
                                            prev.filter(
                                              (cidr) => cidr !== row.cidr
                                            )
                                          );
                                        }
                                      }}
                                    ></Form.Check.Input>
                                    <span className="form-check-sign"></span>
                                  </Form.Check.Label>
                                </Form.Check>
                              );
                            },
                            filterable: false,
                            sortable: false,
                            maxWidth: 80,
                          },
                          {
                            Header: "CIDR",
                            accessor: "cidr",
                            filterable: false,
                            showTooltip: true,
                            sortable: true,
                          },
                          {
                            Header: "Notes",
                            accessor: "note",
                            filterable: false,
                            showTooltip: true,
                            sortable: true,
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        </Card>
        {/* Add CIDR Modal */}
        <Modal
          toggle={() => setAddCidrModalOpen(!addCidrModalOpen)}
          isOpen={addCidrModalOpen}
          scrollable={true}
        >
          <div className="modal-header">
            <h5 className="modal-title">Add CIDR</h5>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setAddCidrModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <Form.Group>
              <Form.Label>CIDR</Form.Label>
              <Form.Control
                id="cidr-input"
                placeholder="CIDR"
                required
                type="text"
                className="form-control"
                name="cidr"
                value={cidrValue}
                onChange={(e) => setCidrValue(e.target.value)}
              ></Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                id="notes-input"
                placeholder="Notes"
                type="text"
                className="form-control"
                name="notes"
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
              ></Form.Control>
            </Form.Group>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setAddCidrModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={addCidr}
              disabled={!cidrValue}
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>
        {/* Delete CIDR Modal */}
        <Modal
          toggle={() => setDeleteCidrModalOpen(!deleteCidrModalOpen)}
          isOpen={deleteCidrModalOpen}
          scrollable={true}
        >
          <div className="modal-header">
            <h5 className="modal-title">
              Delete {`${activeIpTab === "ipv4" ? "IPv4" : "IPv6"}`} CIDR
            </h5>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setDeleteCidrModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <p style={{ color: "#EF4444" }}>
              Deleting the list of{" "}
              {`${activeIpTab === "ipv4" ? "IPv4" : "IPv6"}`} seen below, if you
              have selected any {`${activeIpTab === "ipv4" ? "IPv6" : "IPv4"}`}{" "}
              please delete those separately.
            </p>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={10}
                value={selectedIps.join("\n")}
                readOnly
              />
            </Form.Group>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteCidrModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={deleteCidrs}
              disabled={selectedIps.length === 0}
            >
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
}

export default ManageFirewall;
